'use client';

// src/components/storefront/structured-widgets/ecommerce/CategoryCard.tsx
//
// Category card with multiple design variants.
import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { SafeImage } from '@/components/storefront/SafeImage';
import { getCuratedIcon } from '@/lib/icons/curated';
import type { CategoryVariant, CircleSize } from '@/lib/structured-builder/widget-types/shop-by-category';
type CategoryCardProps = {
  imageUrl: string | null;
  /** LQIP base64 for placeholder="blur"; null pre-backfill rows. */
  blurDataURL?: string | null;
  iconName?: string;
  name: string;
  href: string;
  variant: CategoryVariant;
  productCount?: number;
  cardBg?: string;
  fgColor?: string;
  gradientFrom?: string;
  gradientTo?: string;
  badge?: {
    text: string;
    color: string;
  };
  circleSize?: CircleSize;
};
function getIcon(iconName?: string) {
  return getCuratedIcon(iconName);
}
export function CategoryCard(t0) {
  const $ = _c(27);
  const {
    imageUrl,
    blurDataURL,
    iconName,
    name,
    href,
    variant,
    productCount,
    cardBg,
    fgColor,
    gradientFrom,
    gradientTo,
    badge,
    circleSize
  } = t0;
  let t1;
  if ($[0] !== badge || $[1] !== blurDataURL || $[2] !== cardBg || $[3] !== circleSize || $[4] !== fgColor || $[5] !== gradientFrom || $[6] !== gradientTo || $[7] !== href || $[8] !== iconName || $[9] !== imageUrl || $[10] !== name || $[11] !== productCount) {
    t1 = {
      imageUrl,
      blurDataURL,
      iconName,
      name,
      href,
      productCount,
      cardBg,
      fgColor,
      gradientFrom,
      gradientTo,
      badge,
      circleSize
    };
    $[0] = badge;
    $[1] = blurDataURL;
    $[2] = cardBg;
    $[3] = circleSize;
    $[4] = fgColor;
    $[5] = gradientFrom;
    $[6] = gradientTo;
    $[7] = href;
    $[8] = iconName;
    $[9] = imageUrl;
    $[10] = name;
    $[11] = productCount;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const base = t1;
  switch (variant) {
    case "floating-label":
      {
        let t2;
        if ($[13] !== base) {
          t2 = <FloatingLabel {...base} />;
          $[13] = base;
          $[14] = t2;
        } else {
          t2 = $[14];
        }
        return t2;
      }
    case "circle-icons":
      {
        let t2;
        if ($[15] !== base) {
          t2 = <CircleIcons {...base} />;
          $[15] = base;
          $[16] = t2;
        } else {
          t2 = $[16];
        }
        return t2;
      }
    case "gradient-overlay":
      {
        let t2;
        if ($[17] !== base) {
          t2 = <GradientOverlay {...base} />;
          $[17] = base;
          $[18] = t2;
        } else {
          t2 = $[18];
        }
        return t2;
      }
    case "minimal-card":
      {
        let t2;
        if ($[19] !== base) {
          t2 = <MinimalCard {...base} />;
          $[19] = base;
          $[20] = t2;
        } else {
          t2 = $[20];
        }
        return t2;
      }
    case "banner-strip":
      {
        let t2;
        if ($[21] !== base) {
          t2 = <BannerStrip {...base} />;
          $[21] = base;
          $[22] = t2;
        } else {
          t2 = $[22];
        }
        return t2;
      }
    case "browse-card":
      {
        let t2;
        if ($[23] !== base) {
          t2 = <BrowseCard {...base} />;
          $[23] = base;
          $[24] = t2;
        } else {
          t2 = $[24];
        }
        return t2;
      }
    default:
      {
        let t2;
        if ($[25] !== base) {
          t2 = <FloatingLabel {...base} />;
          $[25] = base;
          $[26] = t2;
        } else {
          t2 = $[26];
        }
        return t2;
      }
  }
}
type CardProps = {
  imageUrl: string | null;
  blurDataURL?: string | null;
  iconName?: string;
  name: string;
  href: string;
  productCount?: number;
  cardBg?: string;
  fgColor?: string;
  gradientFrom?: string;
  gradientTo?: string;
  badge?: {
    text: string;
    color: string;
  };
  circleSize?: CircleSize;
};

// ── Variant 1: Floating Label (current design) ──

function FloatingLabel(t0) {
  const $ = _c(17);
  const {
    imageUrl,
    blurDataURL,
    iconName,
    name,
    href
  } = t0;
  let t1;
  if ($[0] !== iconName) {
    t1 = getIcon(iconName);
    $[0] = iconName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const Icon = t1;
  let t2;
  if ($[2] !== blurDataURL || $[3] !== imageUrl || $[4] !== name) {
    t2 = imageUrl ? <SafeImage src={imageUrl} alt={name} fill={true} sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw" className="object-cover transition-transform duration-500 group-hover:scale-105" blurDataURL={blurDataURL} /> : <div className="absolute inset-0 bg-gray-200" />;
    $[2] = blurDataURL;
    $[3] = imageUrl;
    $[4] = name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Icon) {
    t3 = Icon && <Icon className="h-8 w-8 text-gray-700" weight="light" />;
    $[6] = Icon;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== name) {
    t4 = <span className="text-base font-semibold text-gray-900">{name}</span>;
    $[8] = name;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="absolute bottom-3 inset-x-3 bg-white rounded-xl px-4 py-4 flex flex-col items-center gap-1.5 shadow-sm">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== href || $[14] !== t2 || $[15] !== t5) {
    t6 = <Link href={href} className="group block relative aspect-square rounded-2xl overflow-hidden">{t2}{t5}</Link>;
    $[13] = href;
    $[14] = t2;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}

// ── Variant 2: Circle Icons ──
// Large gradient circles with product images (transparent PNGs blend best)

// Circle diameter per size. lg/xl grow with the viewport: a phone shows two
// circles per row, so they stay at 150px there.
const CIRCLE_SIZE: Record<CircleSize, {
  cls: string;
  px: number;
}> = {
  md: {
    cls: 'w-[140px] h-[140px]',
    px: 140
  },
  lg: {
    cls: 'w-[150px] h-[150px] md:w-[180px] md:h-[180px]',
    px: 180
  },
  xl: {
    cls: 'w-[150px] h-[150px] md:w-[200px] md:h-[200px] lg:w-[240px] lg:h-[240px]',
    px: 240
  }
};
function CircleIcons(t0) {
  const $ = _c(21);
  const {
    imageUrl,
    blurDataURL,
    name,
    href,
    gradientFrom,
    gradientTo,
    circleSize: t1
  } = t0;
  const circleSize = t1 === undefined ? "md" : t1;
  const size = CIRCLE_SIZE[circleSize] ?? CIRCLE_SIZE.md;
  const noFill = gradientFrom === "transparent" && gradientTo === "transparent";
  const t2 = `relative ${size.cls} rounded-full overflow-hidden border-2 border-transparent group-hover:border-primary transition-all bg-gradient-to-b from-[var(--circle-from)] to-[var(--circle-to)] ${noFill ? "" : "shadow-sm group-hover:shadow-md"}`;
  const t3 = gradientFrom ?? "#c4b5fd";
  const t4 = gradientTo ?? "#f9a8d4";
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = {
      "--circle-from": t3,
      "--circle-to": t4
    };
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const t6 = t5 as React.CSSProperties;
  let t7;
  if ($[3] !== t6) {
    t7 = {
      style: t6
    };
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== blurDataURL || $[6] !== imageUrl || $[7] !== name || $[8] !== noFill || $[9] !== size.px) {
    t8 = imageUrl ? <SafeImage src={imageUrl} alt={name} width={size.px} height={size.px} blurDataURL={blurDataURL} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" /> : <div className="w-full h-full flex items-center justify-center"><span className={`text-3xl font-bold ${noFill ? "text-gray-400" : "text-white/60"}`}>{name[0]}</span></div>;
    $[5] = blurDataURL;
    $[6] = imageUrl;
    $[7] = name;
    $[8] = noFill;
    $[9] = size.px;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t2 || $[12] !== t7 || $[13] !== t8) {
    t9 = <div className={t2} {...t7}>{t8}</div>;
    $[11] = t2;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== name) {
    t10 = <span className="text-sm font-medium text-gray-800">{name}</span>;
    $[15] = name;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== href || $[18] !== t10 || $[19] !== t9) {
    t11 = <Link href={href} className="group flex flex-col items-center gap-6 mx-auto">{t9}{t10}</Link>;
    $[17] = href;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}

// ── Variant 3: Gradient Overlay ──

function GradientOverlay(t0) {
  const $ = _c(18);
  const {
    imageUrl,
    blurDataURL,
    iconName,
    name,
    href
  } = t0;
  let t1;
  if ($[0] !== iconName) {
    t1 = getIcon(iconName);
    $[0] = iconName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const Icon = t1;
  let t2;
  if ($[2] !== blurDataURL || $[3] !== imageUrl || $[4] !== name) {
    t2 = imageUrl ? <SafeImage src={imageUrl} alt={name} fill={true} sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw" className="object-cover transition-transform duration-500 group-hover:scale-105" blurDataURL={blurDataURL} /> : <div className="absolute inset-0 bg-gray-300" />;
    $[2] = blurDataURL;
    $[3] = imageUrl;
    $[4] = name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== Icon) {
    t4 = Icon && <Icon className="h-6 w-6 text-white/80" weight="light" />;
    $[7] = Icon;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== name) {
    t5 = <span className="text-lg font-bold text-white">{name}</span>;
    $[9] = name;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className="absolute bottom-0 inset-x-0 p-5 flex flex-col items-start gap-1">{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== href || $[15] !== t2 || $[16] !== t6) {
    t7 = <Link href={href} className="group block relative aspect-[4/5] rounded-2xl overflow-hidden">{t2}{t3}{t6}</Link>;
    $[14] = href;
    $[15] = t2;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}

// ── Variant 4: Minimal Card ──

function MinimalCard(t0) {
  const $ = _c(10);
  const {
    imageUrl,
    blurDataURL,
    name,
    href
  } = t0;
  let t1;
  if ($[0] !== blurDataURL || $[1] !== imageUrl || $[2] !== name) {
    t1 = <div className="relative aspect-[4/3] overflow-hidden">{imageUrl ? <SafeImage src={imageUrl} alt={name} fill={true} sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw" className="object-cover transition-transform duration-500 group-hover:scale-105" blurDataURL={blurDataURL} /> : <div className="w-full h-full bg-gray-200" />}</div>;
    $[0] = blurDataURL;
    $[1] = imageUrl;
    $[2] = name;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== name) {
    t2 = <div className="px-4 py-3 text-center"><span className="text-sm font-semibold text-gray-900">{name}</span></div>;
    $[4] = name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== href || $[7] !== t1 || $[8] !== t2) {
    t3 = <Link href={href} className="group block rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">{t1}{t2}</Link>;
    $[6] = href;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

// ── Variant 5: Banner Strip ──
// Horizontal card: icon | separator | title + product count
// 3 cols desktop, 2 tablet, 1 mobile

function BannerStrip(t0) {
  const $ = _c(24);
  const {
    iconName,
    name,
    href,
    productCount,
    cardBg,
    fgColor
  } = t0;
  let t1;
  if ($[0] !== iconName) {
    t1 = getIcon(iconName);
    $[0] = iconName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const Icon = t1;
  const t2 = cardBg ?? "#f7f5fa";
  const t3 = fgColor ?? "#6b7280";
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = {
      "--card-bg": t2,
      "--card-fg": t3
    };
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = t4 as React.CSSProperties;
  let t6;
  if ($[5] !== t5) {
    t6 = {
      style: t5
    };
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== Icon) {
    t7 = Icon && <div className="w-[42px] h-[42px]"><Icon className="w-full h-full text-[var(--card-fg)]" weight="light" /></div>;
    $[7] = Icon;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t7) {
    t8 = <div className="shrink-0 p-6">{t7}</div>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="shrink-0 w-px h-10 bg-gray-300" />;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== name) {
    t10 = <h3 className="text-base font-bold mb-0.5 text-[var(--card-fg)]">{name}</h3>;
    $[12] = name;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== productCount) {
    t11 = productCount !== undefined && <p className="text-sm text-[var(--card-fg)] opacity-60">{productCount} Products Available</p>;
    $[14] = productCount;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = <div className="ps-4 pe-6 py-6">{t10}{t11}</div>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== href || $[20] !== t12 || $[21] !== t6 || $[22] !== t8) {
    t13 = <Link href={href} className="group flex items-center rounded-2xl border border-transparent hover:border-primary transition-all bg-[var(--card-bg)]" {...t6}>{t8}{t9}{t12}</Link>;
    $[19] = href;
    $[20] = t12;
    $[21] = t6;
    $[22] = t8;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
}

// ── Variant 6: Browse Card ──
// Gray image zone on top, white name zone on bottom, thin separator between

function BrowseCard(t0) {
  const $ = _c(23);
  const {
    imageUrl,
    blurDataURL,
    iconName,
    name,
    href,
    badge
  } = t0;
  let t1;
  if ($[0] !== iconName) {
    t1 = getIcon(iconName);
    $[0] = iconName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const Icon = t1;
  let t2;
  if ($[2] !== blurDataURL || $[3] !== imageUrl || $[4] !== name) {
    t2 = imageUrl ? <SafeImage src={imageUrl} alt={name} fill={true} sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw" className="object-cover transition-transform duration-500 group-hover:scale-105" blurDataURL={blurDataURL} /> : <div className="w-20 h-20 rounded-xl bg-gray-200" />;
    $[2] = blurDataURL;
    $[3] = imageUrl;
    $[4] = name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== badge) {
    t3 = badge && <span className="absolute top-3 end-3 text-[10px] font-bold text-white px-2.5 py-1 rounded bg-[var(--badge-bg)]" {...{
      style: {
        "--badge-bg": badge.color
      } as React.CSSProperties
    }}>{badge.text}</span>;
    $[6] = badge;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <div className="relative aspect-square bg-white">{t2}{t3}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="border-t border-[#e8e8eb]" />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== Icon) {
    t6 = Icon && <Icon className="h-5 w-5 text-gray-500 shrink-0" weight="light" />;
    $[12] = Icon;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== name) {
    t7 = <span className="text-[15px] font-medium text-gray-800">{name}</span>;
    $[14] = name;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div className="flex items-center justify-center gap-2.5 px-4 py-4">{t6}{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== href || $[20] !== t4 || $[21] !== t8) {
    t9 = <Link href={href} className="group block rounded-[14px] overflow-hidden border border-[#e8e8eb] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow">{t4}{t5}{t8}</Link>;
    $[19] = href;
    $[20] = t4;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}